import React, { Fragment, useState } from 'react';
import { OrderCardProduct } from './dashboard-utils';
import { Typography } from '../../../components/typography/typography';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { Job, JobType } from '../../../clients/quipli-service/job-tasks/job-tasks';
import Icon from '../../../components/icons/icon';
import { Tooltip } from '../../../components/tooltip/tooltip';
import {
  areUnitTasksComplete,
  areUnitTasksSuccessful,
} from '../../job-tasks/inspection/components/unit-inspection-view/utils';
import { IconNames } from '../../../components/icons/icons';

type ProductTableProps = {
  orderProducts: OrderCardProduct[];
  unitTrackingViaUnitNum?: boolean;
  jobs?: Job[];
};

export const ProductTable = ({ orderProducts, unitTrackingViaUnitNum = true, jobs }: ProductTableProps) => {
  const [expanded, setExpanded] = useState(false);

  const MAX_PRODUCTS = 10;
  const numProducts = orderProducts.length;
  const numOverflow = numProducts - MAX_PRODUCTS;

  return (
    <div className="order-products grid grid-cols-1 rounded-4 overflow-hidden mb-2">
      <div className="order-products-header flex flex-row bg-grey-100 p-2">
        <div style={{ flex: 1 }}></div>
        <div style={{ flex: 6 }}>Product name</div>
        <div style={{ flex: 2 }}>Unit</div>
      </div>

      <div className="overflow-scroll max-h-[25rem]">
        {orderProducts.slice(0, !expanded ? MAX_PRODUCTS : undefined).map((product, index) => (
          <div className="flex flex-row p-2" key={index}>
            <div style={{ flex: 1 }}>
              <Typography type="text-sm">{product.quantity} x</Typography>
            </div>
            <div style={{ flex: 6 }}>
              <Typography type="text-sm" weight="semi-bold">
                {product.name}
              </Typography>
            </div>
            <div style={{ flex: 2 }}>
              <ProductUnitsCell {...product} unitTrackingViaUnitNum={unitTrackingViaUnitNum} jobs={jobs} />
            </div>
          </div>
        ))}
      </div>
      {numOverflow > 0 && (
        <div className="flex flex-row p-2" onClick={() => setExpanded((last) => !last)}>
          <div style={{ flex: 1 }} />
          <div style={{ flex: 6 }}>
            <Typography type="text-xs" weight="regular" className="text-quipli-black-500 cursor-pointer">
              {!expanded ? `+ ${numOverflow} items` : '- Less items'}
            </Typography>
          </div>
          <div style={{ flex: 2 }} />
        </div>
      )}
    </div>
  );
};

const ProductUnitsCell = ({
  id,
  trackingType,
  quantity,
  assignedUnits,
  unitTrackingViaUnitNum,
  jobs,
}: OrderCardProduct & { unitTrackingViaUnitNum: boolean; jobs?: Job[] }) => {
  const remainingUnitsToAssign = quantity - assignedUnits.length;

  return trackingType === ProductTrackingType.SerialUnits && id != 1 ? (
    <div>
      {assignedUnits.map((ui) => (
        <ProductUnitInfo
          key={`dashboard-card-product-unit-id-${ui.id}`}
          id={ui.id}
          unitSerial={unitTrackingViaUnitNum ? ui.unitNumber : ui.serialNumber}
          jobs={jobs?.filter((job) => job.tasks.filter((task) => task.metadata?.productUnitId === ui.id).length)}
        />
      ))}

      {remainingUnitsToAssign > 0 && (
        <div>
          <Typography type="text-sm" weight="semi-bold" className="text-error-500">
            {remainingUnitsToAssign > 1 ? `${quantity - assignedUnits.length} units not assigned` : 'Not assigned'}
          </Typography>
        </div>
      )}
    </div>
  ) : null;
};

const ProductUnitInfo = ({ id, unitSerial, jobs }: { id: number; unitSerial: string; jobs?: Job[] }) => {
  const sortedJobs =
    jobs
      ?.filter((job) => job.tasks.filter((task) => task.metadata?.productUnitId === id).length)
      .sort((a) => (a.type === JobType.CheckoutInspection ? -1 : 1)) || [];

  return (
    <div>
      <div className="flex gap-1">
        <Typography type="text-sm" weight="semi-bold" className="grow">
          {unitSerial}
        </Typography>
        {sortedJobs.map((job) => (
          <Fragment key={`dashboard-card-product-unit-id-${id}-job-${job.id}`}>
            <ProductUnitInspectionStatus unitId={id} job={job} />
          </Fragment>
        ))}
      </div>
    </div>
  );
};

const ProductUnitInspectionStatus = ({ unitId, job }: { unitId: number; job: Job }) => {
  const isComplete = areUnitTasksComplete(job.tasks, unitId);
  const isSuccessful = areUnitTasksSuccessful(job.tasks, unitId);

  const tooltip = `Check-${job.type === JobType.CheckoutInspection ? 'out' : 'in'} inspection ${
    isComplete ? `${isSuccessful ? '' : 'not '}passed` : 'pending'
  }`;
  const icon: { name: IconNames; color: string } = {
    name: isComplete ? (isSuccessful ? 'CheckCircle' : 'XCircle') : 'InfoCircle',
    color: `var(--${isComplete ? (isSuccessful ? 'success-500' : 'error-500') : 'warning-500'})`,
  };

  return (
    <>
      <Tooltip tooltip={tooltip} type="black">
        <Icon icon={icon.name} stroke={icon.color} strokeWidth={2} />
      </Tooltip>
    </>
  );
};
